import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { TaskflowGraph } from './TaskflowGraph';

const STORAGE_OPEN = 'taskflow.graphPanel.open';
const STORAGE_WIDTH = 'taskflow.graphPanel.width';
const DEFAULT_WIDTH_PCT = 50;
const MIN_PCT = 30;
const MAX_PCT = 90;

interface Props {
  textareaId: string;
}

function readStoredOpen(): boolean {
  return window.localStorage.getItem(STORAGE_OPEN) === '1';
}
function readStoredWidth(): number {
  const v = parseFloat(window.localStorage.getItem(STORAGE_WIDTH) ?? '');
  if (!Number.isFinite(v)) return DEFAULT_WIDTH_PCT;
  return Math.min(MAX_PCT, Math.max(MIN_PCT, v));
}

export function TaskflowGraphPanel({ textareaId }: Props): React.JSX.Element {
  const [open, setOpen] = useState<boolean>(readStoredOpen);
  const [widthPct, setWidthPct] = useState<number>(readStoredWidth);
  const [xml, setXml] = useState<string>('');
  const [renderKey, setRenderKey] = useState(0);
  const dragging = useRef(false);

  // Read XML — prefer the Ace editor (live source of truth), fall back to the textarea.
  const captureXml = useCallback(() => {
    const ta = document.getElementById(textareaId) as HTMLTextAreaElement | null;
    let value = ta?.value ?? '';
    const ace = (window as unknown as { ace?: { edit: (el: Element) => { getValue: () => string } } }).ace;
    if (ace && ta) {
      // The Ace editor is the textarea's next sibling (an empty div Ace was attached to).
      const aceContainer = ta.nextElementSibling;
      if (aceContainer) {
        try {
          value = ace.edit(aceContainer).getValue();
        } catch {
          // ignore, fall back to textarea
        }
      }
    }
    setXml(value);
    setRenderKey(k => k + 1);
  }, [textareaId]);

  useEffect(() => {
    if (open) captureXml();
  }, [open, captureXml]);

  useEffect(() => {
    window.localStorage.setItem(STORAGE_OPEN, open ? '1' : '0');
  }, [open]);

  useEffect(() => {
    window.localStorage.setItem(STORAGE_WIDTH, String(widthPct));
  }, [widthPct]);

  // Drag-to-resize from the panel's left edge
  useEffect(() => {
    function onMove(e: MouseEvent): void {
      if (!dragging.current) return;
      const vw = window.innerWidth;
      const fromRight = vw - e.clientX;
      const pct = (fromRight / vw) * 100;
      setWidthPct(Math.min(MAX_PCT, Math.max(MIN_PCT, pct)));
    }
    function onUp(): void {
      dragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  const startDrag = (e: React.MouseEvent): void => {
    e.preventDefault();
    dragging.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  return (
    <>
      <button
        type="button"
        className="btn btn-outline-primary taskflow-graph-toggle"
        onClick={() => setOpen(o => !o)}
      >
        <i className="fa-solid fa-diagram-project" /> {open ? 'Hide graph' : 'Show graph'}
      </button>

      {open && createPortal(
        <aside
          className="taskflow-graph-panel"
          style={{ width: `${widthPct}vw` }}
          aria-label="Taskflow graph"
        >
          <div
            className="taskflow-graph-panel__resizer"
            onMouseDown={startDrag}
            title="Drag to resize"
          />
          <div className="taskflow-graph-panel__header">
            <strong>Taskflow visualization</strong>
            <div className="taskflow-graph-panel__actions">
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={captureXml}
                title="Re-parse the XML from the textarea"
              >
                <i className="fa-solid fa-arrows-rotate" /> Re-render
              </button>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={() => setOpen(false)}
              />
            </div>
          </div>
          <div className="taskflow-graph-panel__body">
            <TaskflowGraph key={renderKey} xml={xml} />
          </div>
        </aside>,
        document.body
      )}
    </>
  );
}
